import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronDown, LayoutGrid, Newspaper, Users, Star, ExternalLink, Info,
  Receipt, ScrollText, GraduationCap, CheckSquare, ShieldCheck,
  CalendarRange, CalendarDays, UserCircle2, LogOut, Languages, Sun,
  Eye, EyeOff, ArrowRight, Search, CreditCard, FileText, Image as ImageIcon,
  Cloud, CloudSun, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning,
  Instagram, Lightbulb, BookOpen, Route, ClipboardCheck,
} from 'lucide-react';
import { fetchWeather, describeCode } from '../lib/weather';
import { useAppCatalogue, GROUP_ORDER as CATALOGUE_GROUP_ORDER } from '../contexts/AppCatalogueContext';
import { localizedApp } from '../lib/appLocale';
import SearchPalette from './SearchPalette';
import ProfilePhotoModal from './ProfilePhotoModal';
import { useAuth } from '../contexts/AuthProvider';
import { useLocale, useT } from '../contexts/LocaleProvider';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { useReviewQueue } from '../contexts/ReviewQueueContext';
import { LT } from '../lib/appCatalogue';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { deptLabel, setDepartmentCatalogue } from '../lib/departments';
import api from '../utils/api';

/**
 * Unified app shell · one persistent topbar across the hub landing,
 * the HR pages and the admin zone. The Apps ▾ mega-menu launches any
 * LampTwist app; the RH ▾ menu drops down the HR features (Accueil,
 * Notes de frais, Annuaire, Ressources RH, Manager queue, Admin).
 *
 * Drop-in replacement for the previous left-sidebar Shell · same props.
 */

export default function TopBarShell({
  children,
  reviewCount: reviewCountProp,
  wide = false,
  headerAction = null,
}) {
  // The review-queue context is the source of truth · pages can still pass
  // a `reviewCount` prop for backward compat, but the badge is now visible
  // on every page (including the hub landing) without per-page wiring.
  const { count: ctxReviewCount } = useReviewQueue();
  const reviewCount = reviewCountProp != null ? reviewCountProp : ctxReviewCount;
  const { user, logout } = useAuth();
  const t = useT();
  const { locale, setLocale, supported, labels, intlLocale } = useLocale();
  const {
    previewing, stop: stopPreview,
    actAsEmail, actAsName, stopActingAs,
  } = useImpersonation();
  const [realAdmin, setRealAdmin] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  /* Cmd-K / Ctrl-K opens the global search palette · standard shortcut
   * across modern intranets so power users get muscle-memory wins. */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // Real impersonation overrides admin perms server-side · the UI follows
  // suit by hiding admin items while acting as someone else.
  const isAdmin = realAdmin && !previewing && !actAsEmail;

  const [canEditContent, setCanEditContent] = useState(false);
  const [realExpenseManager, setRealExpenseManager] = useState(false);
  // Who may open the Beheer panel · full admins, plus expense managers
  // (accounting) who get a scoped view of just the Onkostennota's tab.
  // Suppressed while previewing / acting-as, exactly like isAdmin.
  const showBeheer = (realAdmin || realExpenseManager) && !previewing && !actAsEmail;
  useEffect(() => {
    api.adminMe().then(d => {
      setRealAdmin(!!d?.is_admin);
      setRealExpenseManager(!!d?.is_expense_manager);
    }).catch(() => { setRealAdmin(false); setRealExpenseManager(false); });
    // Hydrate dept catalogue so deptLabel(code, locale) works app-wide.
    api.departments().then(setDepartmentCatalogue).catch(() => {});
    // Employee profile · used to surface role + department in the topbar.
    api.myEmployee().then(setEmployee).catch(() => setEmployee(null));
    // MANEVA CMS gate · enables the dropdown shortcut for content editors
    // who aren't full admins.
    api.canEditContent().then(setCanEditContent).catch(() => setCanEditContent(false));
  }, []);

  /* Light mode is the only supported theme for now — the brand palette
   * (warm beige + sage green) was tuned for light surfaces and the dark
   * variant left several pages looking off. Force-strip the .dark class
   * and clear any stale localStorage so returning users come back to
   * light. The user-menu toggle is gone too. */
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    if (localStorage.getItem('theme') === 'dark') {
      localStorage.removeItem('theme');
    }
  }, []);

  // Greeting is now used only on the splash hero (passed via context-free
  // page state) · the topbar identity line just shows the user's name.
  // Kept the import-side hook reference removed via the pickGreeting drop.
  const todayLabel = useMemo(
    () => new Intl.DateTimeFormat(intlLocale, { weekday: 'long', day: 'numeric', month: 'long' })
      .format(new Date()),
    [intlLocale],
  );
  const firstName = (user?.displayName || '').split(' ')[0];
  const initials = (user?.displayName || 'L L').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();

  // One width for the whole app · 1480 reads comfortably on a 1440p
  // display without feeling sparse on smaller screens and keeps admin
  // tables and the hub sidebar aligned page-to-page.
  const maxW = 'max-w-[1480px]';
  void wide;  // legacy prop, kept for API parity

  return (
    <div className="min-h-screen flex flex-col"
         style={{ background: LT.beigeWarm, color: LT.greenDeep }}>

      {/* Topbar */}
      <TopBar
        t={t} maxW={maxW}
        isAdmin={isAdmin}
        showBeheer={showBeheer}
        canEditContent={canEditContent}
        reviewCount={reviewCount}
        firstName={firstName} todayLabel={todayLabel}
        user={user} initials={initials} employee={employee}
        locale={locale} setLocale={setLocale} supported={supported} labels={labels}
        logout={logout}
        onOpenSearch={() => setSearchOpen(true)}
        onOpenProfile={() => setProfileOpen(true)}
      />
      <SearchPalette
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        isAdmin={isAdmin}
      />
      <ProfilePhotoModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onUpdated={(emp) => setEmployee(emp)}
      />

      {/* Preview banner · admin sees the regular-employee UI */}
      {previewing && realAdmin && !actAsEmail && (
        <div className="text-white text-[13px]" style={{ background: '#a23d2c' }}>
          <div className={`${maxW} mx-auto px-8 py-2 flex items-center gap-3`}>
            <Eye className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1 truncate">{t('preview.banner')}</span>
            <button type="button" onClick={stopPreview}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 transition">
              <EyeOff className="h-3.5 w-3.5" /> {t('preview.exit')}
            </button>
          </div>
        </div>
      )}

      {/* Act-as banner · all requests are tagged for the impersonated user.
          Distinct color (deeper red) so it's not confused with the preview. */}
      {actAsEmail && (
        <div className="text-white text-[13px]" style={{ background: '#7a1f1f' }}>
          <div className={`${maxW} mx-auto px-8 py-2 flex items-center gap-3`}>
            <Eye className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1 truncate">
              {t('actAs.banner', { who: actAsName || actAsEmail })}
            </span>
            <button type="button" onClick={stopActingAs}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 transition">
              <EyeOff className="h-3.5 w-3.5" /> {t('actAs.exit')}
            </button>
          </div>
        </div>
      )}

      {/* Optional page-level header action band (pages can pass a CTA) */}
      {headerAction && (
        <div className={`${maxW} mx-auto px-8 pt-3 flex items-center justify-end`}>
          {headerAction}
        </div>
      )}

      {/* Page body */}
      <main className={`${maxW} mx-auto px-8 py-6 w-full flex-1`}>
        {children}
      </main>

    </div>
  );
}

/* ── Top bar ────────────────────────────────────────────────────── */

const TopBar = ({
  t, maxW, isAdmin, showBeheer, canEditContent, reviewCount,
  firstName, todayLabel,
  user, initials, employee,
  locale, setLocale, supported, labels,
  logout,
  onOpenSearch,
  onOpenProfile,
}) => {
  // Subtitle under the user's name in the topbar · role · department.
  // Date stays as the fallback when no employee row is available yet.
  const subtitle = employee
    ? [employee.poste, employee.departement ? deptLabel(employee.departement, locale) : null]
        .filter(Boolean).join(' · ')
    : todayLabel;
  const [appsOpen, setAppsOpen] = useState(false);
  const [hrOpen, setHrOpen]     = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  // The Career column only shows once the caller can see a career map
  // (HR / admins while it's unpublished, everyone after).
  const [careerVisible, setCareerVisible] = useState(false);
  useEffect(() => {
    api.careerMaps().then(d => setCareerVisible((d?.maps || []).length > 0)).catch(() => setCareerVisible(false));
  }, []);
  const wrapRef = useRef(null);
  const { favorites, toggle: toggleFavorite } = useFavorites();

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setAppsOpen(false);
        setHrOpen(false);
        setGuidesOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') { setAppsOpen(false); setHrOpen(false); setGuidesOpen(false); }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  const { apps: allApps } = useAppCatalogue();
  // Only featured apps appear in the mega-menu · the rest live on /apps.
  const featuredByGroup = useMemo(() => {
    const g = Object.fromEntries(CATALOGUE_GROUP_ORDER.map(k => [k, []]));
    allApps.filter(a => a.is_featured).forEach(a => {
      (g[a.group] || (g[a.group] = [])).push(a);
    });
    return g;
  }, [allApps]);
  const hiddenCount = useMemo(
    () => allApps.filter(a => !a.is_featured).length,
    [allApps],
  );

  const openApps   = () => { setAppsOpen(o => !o);   setHrOpen(false);   setGuidesOpen(false); };
  const openHr     = () => { setHrOpen(o => !o);     setAppsOpen(false); setGuidesOpen(false); };
  const openGuides = () => { setGuidesOpen(o => !o); setAppsOpen(false); setHrOpen(false); };

  return (
    <header className="sticky top-0 z-30"
            style={{
              // Slim paper bar with a hairline (LampTwist: no blur on
              // chrome, no coloured ribbon · the logo carries the brand).
              background: LT.beigeLight,
              borderBottom: '1px solid rgba(43, 65, 56, 0.12)',
            }}>
      <div className={`${maxW} mx-auto px-8 py-3 flex items-center gap-1`} ref={wrapRef}>
        <Link to="/"
              className="inline-flex items-center gap-2 mr-4 shrink-0"
              aria-label="LampTwist hub">
          <img
            src="/lamptwist-logo.svg"
            alt="LampTwist"
            className="h-7 w-auto"
            style={{ display: 'block' }}
          />
          <span className="font-display tracking-tight text-[18px] leading-none"
                style={{ color: LT.green50 }}>
            ·hub
          </span>
        </Link>

        {/* Apps dropdown */}
        <div className="relative">
          <TopBarButton
            active={appsOpen}
            onClick={openApps}
            aria-expanded={appsOpen}
          >
            <LayoutGrid className="h-4 w-4" />
            {t('splash.nav.apps')}
            <ChevronDown className={`h-4 w-4 transition ${appsOpen ? 'rotate-180' : ''}`} />
          </TopBarButton>
          {appsOpen && (
            <AppsMegaMenu
              t={t}
              appByGroup={featuredByGroup}
              hiddenCount={hiddenCount}
              favorites={favorites}
              onToggleFavorite={toggleFavorite}
              onClose={() => setAppsOpen(false)}
            />
          )}
        </div>

        {/* RH dropdown */}
        <div className="relative">
          <TopBarButton
            active={hrOpen}
            onClick={openHr}
            aria-expanded={hrOpen}
          >
            <UserCircle2 className="h-4 w-4" />
            {t('splash.nav.hr')}
            {reviewCount > 0 && (
              <span className="ml-0.5 text-[10px] font-medium rounded-full px-1.5 py-0.5"
                    style={{ background: LT.greenDeep, color: LT.beigeLight, minWidth: 18, textAlign: 'center' }}>
                {reviewCount}
              </span>
            )}
            <ChevronDown className={`h-4 w-4 transition ${hrOpen ? 'rotate-180' : ''}`} />
          </TopBarButton>
          {hrOpen && (
            <HrMegaMenu
              t={t}
              careerVisible={careerVisible}
              isAdmin={isAdmin}
              showBeheer={showBeheer}
              reviewCount={reviewCount}
              onClose={() => setHrOpen(false)}
            />
          )}
        </div>

        {/* Reading · newsletters and guides, their own category rather than
            HR resources. The guides are static pages (public/guides/…). */}
        <div className="relative">
          <TopBarButton active={guidesOpen} onClick={openGuides} aria-expanded={guidesOpen}>
            <BookOpen className="h-4 w-4" />
            {t('nav.read')}
            <ChevronDown className={`h-4 w-4 transition ${guidesOpen ? 'rotate-180' : ''}`} />
          </TopBarButton>
          {guidesOpen && <ReadingMenu t={t} onClose={() => setGuidesOpen(false)} />}
        </div>

        <TopBarAnchor href="/#team" icon={Users}     label={t('splash.nav.directory')} />

        {/* Right side · search + weather + identity + user menu (admin link
            lives inside the user dropdown now to keep this row uncluttered). */}
        <div className="ml-auto flex items-center gap-3 min-w-0">
          <SearchTrigger t={t} onClick={onOpenSearch} />
          <WeatherChip />
          <InstagramChip />
          <div className="hidden md:flex flex-col items-end leading-tight min-w-0">
            <span className="text-[12.5px] truncate" style={{ color: LT.greenDeep }}>
              {user?.displayName || firstName || ''}
            </span>
            <span className="text-[10.5px] truncate" style={{ color: LT.green50 }}>
              {subtitle}
            </span>
          </div>

          <UserDropdown
            t={t}
            user={user}
            initials={initials}
            isAdmin={isAdmin}
            showBeheer={showBeheer}
            canEditContent={canEditContent}
            locale={locale}
            setLocale={setLocale}
            supported={supported}
            labels={labels}
            logout={logout}
            onOpenProfile={onOpenProfile}
          />
        </div>
      </div>
    </header>
  );
};

/* Search trigger · styled like a real input so the affordance reads as
 * "type to search" rather than "click to open dialog". Wider since the
 * admin shortcut moved into the user dropdown, freeing topbar space. */
const SearchTrigger = ({ t, onClick }) => {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPod|iPad/i.test(navigator.platform || '');
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('search.label')}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-[13px] transition hover:bg-black/[0.04] md:min-w-[260px] lg:min-w-[320px]"
      style={{ color: LT.green50, border: `1px solid ${LT.green30}`, background: LT.beigeLight }}
    >
      <Search className="h-4 w-4 shrink-0" />
      <span className="hidden md:inline flex-1 text-left truncate">{t('search.placeholder')}</span>
      <kbd className="hidden md:inline-block px-1.5 py-0.5 rounded font-num text-[10.5px] ml-auto"
           style={{ border: `1px solid ${LT.green30}`, color: LT.green50, background: 'transparent' }}>
        {isMac ? '⌘K' : 'Ctrl K'}
      </kbd>
    </button>
  );
};

/* Subtle current-weather chip · icon + °C. Open-Meteo, no API key.
 * Hides silently on fetch errors so a network blip doesn't leave a
 * broken icon in the topbar. */
const WEATHER_ICONS = { Cloud, CloudSun, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning, Sun };

const WeatherChip = () => {
  const [w, setW] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchWeather()
      .then(d => { if (!cancelled) setW(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!w) return null;
  const { icon, label } = describeCode(w.code);
  const Icon = WEATHER_ICONS[icon] || Cloud;
  return (
    <span
      className="hidden sm:inline-flex items-center gap-1 text-[12.5px]"
      style={{ color: LT.green50 }}
      title={`${label} · ${w.temperature}°C`}
      aria-label={`Weather · ${label}, ${w.temperature}°C`}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="font-num">{w.temperature}°</span>
    </span>
  );
};

/* Quiet Instagram link · sits between the weather chip and the user
 * identity. Discoverable but not loud · matches WeatherChip sizing
 * so the row stays balanced. */
const InstagramChip = () => (
  <a
    href="https://www.instagram.com/lamptwist_official/"
    target="_blank"
    rel="noreferrer"
    aria-label="@lamptwist_official sur Instagram"
    title="@lamptwist_official"
    className="hidden sm:inline-flex items-center transition hover:opacity-75"
    style={{ color: LT.green50 }}
  >
    <Instagram className="h-4 w-4" />
  </a>
);

/* Nav items used to read as plain text · feedback was that people didn't
 * notice they were interactive. Bumped to 14.5px / font-medium and a
 * visible hover/active background so the row clearly looks like a menu. */
const TopBarButton = ({ active, onClick, children, ...rest }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[14.5px] font-medium transition hover:bg-black/[0.06]"
    style={{ color: LT.greenDeep, background: active ? 'rgba(43,65,56,0.10)' : 'transparent' }}
    {...rest}
  >
    {children}
  </button>
);

const TopBarAnchor = ({ href, icon: Icon, label }) => {
  const loc = useLocation();
  const cls = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[14.5px] font-medium transition hover:bg-black/[0.06]';
  // When already on / · use native anchor scroll. From other pages · navigate.
  if (loc.pathname === '/' && href.startsWith('/#')) {
    return (
      <a href={href} className={cls} style={{ color: LT.greenDeep }}>
        <Icon className="h-4 w-4" /> {label}
      </a>
    );
  }
  return (
    <Link to={href} className={cls} style={{ color: LT.greenDeep }}>
      <Icon className="h-4 w-4" /> {label}
    </Link>
  );
};

/* ── Apps mega menu ─────────────────────────────────────────────── */

const AppsMegaMenu = ({ t, appByGroup, hiddenCount, favorites, onToggleFavorite, onClose }) => {
  const visibleGroups = CATALOGUE_GROUP_ORDER.filter(k => (appByGroup[k] || []).length > 0);
  return (
    <div
      className="absolute left-0 mt-2 rounded-xl p-5 shadow-lg z-40 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4 min-w-[640px]"
      style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}
      role="menu"
    >
      {visibleGroups.map(groupKey => (
        <div key={groupKey}>
          <div className="text-[10.5px] uppercase tracking-[0.16em] font-medium mb-2"
               style={{ color: LT.green50 }}>
            {t(`splash.group.${groupKey}.eyebrow`)}
          </div>
          <ul className="space-y-0.5">
            {appByGroup[groupKey].map(app => (
              <MegaItem
                key={app.slug}
                app={app}
                t={t}
                isFav={favorites.includes(app.slug)}
                onToggleFavorite={() => onToggleFavorite(app.slug)}
                onClose={onClose}
              />
            ))}
          </ul>
        </div>
      ))}
      <div className="col-span-full pt-3 flex items-center gap-3 flex-wrap"
           style={{ borderTop: `1px solid ${LT.green30}` }}>
        <span className="text-[11px] flex items-center gap-1.5" style={{ color: LT.green50 }}>
          <Star className="h-3 w-3" style={{ color: LT.greenDeep, fill: LT.yellowLight }} />
          {t('splash.fav.menuHint')}
        </span>
        <Link to="/apps" onClick={onClose}
              className="ml-auto inline-flex items-center gap-1.5 text-[12px]"
              style={{ color: LT.greenDeep, fontWeight: 500 }}>
          {hiddenCount > 0 ? t('apps.seeAllN', { n: hiddenCount }) : t('apps.seeAll')}
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
};

const MegaItem = ({ app, t, isFav, onToggleFavorite, onClose }) => {
  const { locale } = useLocale();
  const Icon = app.Icon || app.icon;
  const label = localizedApp(app, locale).name;
  const linkInner = (
    <span className="flex items-center gap-2 px-2 py-1.5 rounded-md transition hover:bg-black/[0.04]"
          style={{ color: LT.greenDeep }}>
      {app.logo
        ? <img src={app.logo} alt="" className="h-3.5 w-3.5 shrink-0 object-contain" />
        : <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: app.dot }} />}
      <span className="text-[13px] flex-1">{label}</span>
    </span>
  );

  const link = app.internal && app.to
    ? <Link to={app.to} onClick={onClose} className="flex-1 min-w-0">{linkInner}</Link>
    : <a href={app.href} target="_blank" rel="noreferrer" onClick={onClose} className="flex-1 min-w-0">{linkInner}</a>;

  return (
    <li className="group flex items-center gap-1">
      {link}
      {/* Info shortcut → app detail page */}
      <Link
        to={`/apps/${app.slug}`}
        onClick={onClose}
        className="p-1 rounded opacity-30 group-hover:opacity-90 transition"
        title={t('apps.moreInfo')}
        aria-label={t('apps.moreInfo')}
      >
        <Info className="h-3.5 w-3.5" style={{ color: LT.green50 }} />
      </Link>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavorite(); }}
        className={`p-1 rounded transition ${isFav ? 'opacity-100' : 'opacity-30 group-hover:opacity-90'}`}
        title={isFav ? t('splash.fav.unpin') : t('splash.fav.pin')}
        aria-label={isFav ? t('splash.fav.unpin') : t('splash.fav.pin')}
      >
        <Star
          className="h-3.5 w-3.5"
          style={{ color: LT.greenDeep, fill: isFav ? LT.yellowLight : 'transparent' }}
        />
      </button>
    </li>
  );
};

/* ── RH (HR) mega menu ──────────────────────────────────────────── */

const HrMegaMenu = ({ t, isAdmin, showBeheer, reviewCount, careerVisible, onClose }) => {
  // Columns: Outils · Carrière · Documents · Manager. Reading (newsletters,
  // guides) lives in its own top-bar menu.
  return (
    <div
      className={`absolute left-0 mt-2 p-5 shadow-lg z-40 grid grid-cols-2 ${careerVisible ? 'sm:grid-cols-4 min-w-[760px]' : 'sm:grid-cols-3 min-w-[600px]'} gap-x-6 gap-y-4`}
      style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}
      role="menu"
    >
      <HrColumn title={t('nav.tools')}>
        <HrItem to="/expenses" icon={Receipt} label={t('nav.expenses')} onClose={onClose} />
        {isAdmin && (
          <HrItem to="/calendar" icon={CalendarRange} label={t('nav.calendar')} onClose={onClose} />
        )}
        {/* External HR portals · visible to everyone. iLeo for leave,
            Monizze for meal vouchers, Group S for payslips. */}
        <HrItemExternal href="https://time2.ileo.com/#/login" icon={CalendarDays}
                        label={t('nav.leave')} onClose={onClose} />
        <HrItemExternal href="https://account.monizze.be/" icon={CreditCard}
                        label="Monizze" onClose={onClose} />
        <HrItemExternal href="https://online.groups.be/portal/" icon={FileText}
                        label="Group S" onClose={onClose} />
      </HrColumn>

      {careerVisible && (
        <HrColumn title={t('nav.career')}>
          <HrItem to="/career-map"                  icon={Route}       label={t('nav.careerMap')}  onClose={onClose} />
          {/* Work in progress · shown so people know it's coming, not clickable yet. */}
          <HrItemSoon icon={ClipboardCheck} label={t('nav.evaluation')} soon={t('nav.soon')} />
        </HrColumn>
      )}

      <HrColumn title={t('nav.documents')}>
        <HrItem to="/resources/training"    icon={GraduationCap} label={t('nav.training')}    onClose={onClose} />
        <HrItem to="/resources/policies"    icon={ScrollText}    label={t('nav.policies')}    onClose={onClose} />
      </HrColumn>

      <HrColumn title={reviewCount > 0 || showBeheer ? t('nav.manager') : ''}>
        {reviewCount > 0 && (
          <HrItem to="/review" icon={CheckSquare} label={t('nav.toReview')}
                  badge={reviewCount} onClose={onClose} />
        )}
        {showBeheer && (
          <HrItem to="/admin" icon={ShieldCheck} label={t('nav.adminPanel')} onClose={onClose} />
        )}
      </HrColumn>
    </div>
  );
};

/* Reading menu · the newsletters and the guides, one card each. Internal
 * pages route in place; the guides are static pages opened in a new tab. */
const READING = [
  { to: '/resources/newsletters', icon: Newspaper, label: 'nav.newsletters', sub: 'nav.newsletters.sub' },
  { href: '/guides/lighting/', icon: Lightbulb, label: 'nav.lightingGuide', sub: 'nav.lightingGuide.sub' },
];

const ReadingMenu = ({ t, onClose }) => (
  <div className="absolute left-0 mt-2 p-3 shadow-lg z-40 w-[360px] max-w-[calc(100vw-2rem)]"
       style={{ background: LT.beigeLight, border: '1px solid rgba(43,65,56,0.12)' }} role="menu">
    {READING.map(g => {
      const inner = (
        <>
          <span className="h-9 w-9 shrink-0 rounded-full grid place-items-center" style={{ background: g.href ? LT.yellow : LT.beigeWarm }}>
            <g.icon className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5 text-[14px]">{t(g.label)} {g.href && <ExternalLink className="h-3 w-3" style={{ color: LT.green50 }} />}</span>
            <span className="block text-[12.5px] leading-snug mt-0.5" style={{ color: LT.green50 }}>{t(g.sub)}</span>
          </span>
        </>
      );
      const cls = 'flex items-start gap-3 p-3 transition hover:bg-black/[0.04]';
      return g.href ? (
        <a key={g.label} href={g.href} target="_blank" rel="noreferrer" onClick={onClose} role="menuitem" className={cls} style={{ color: LT.greenDeep }}>{inner}</a>
      ) : (
        <Link key={g.label} to={g.to} onClick={onClose} role="menuitem" className={cls} style={{ color: LT.greenDeep }}>{inner}</Link>
      );
    })}
  </div>
);

const HrColumn = ({ title, children }) => (
  <div>
    {title && (
      <div className="text-[10.5px] uppercase tracking-[0.16em] font-medium mb-2"
           style={{ color: LT.green50 }}>
        {title}
      </div>
    )}
    <ul className="space-y-0.5">{children}</ul>
  </div>
);

const HrItemSoon = ({ icon: Icon, label, soon }) => (
  <li aria-disabled="true">
    <span className="flex items-center gap-2 px-2 py-1.5 cursor-default" style={{ color: LT.green50 }}>
      <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: LT.green30 }} />
      <span className="text-[13px] flex-1">{label}</span>
      <span className="text-[9.5px] uppercase tracking-[0.1em] rounded-full px-1.5 py-0.5"
            style={{ border: `1px solid ${LT.green30}`, color: LT.green50 }}>{soon}</span>
    </span>
  </li>
);

const HrItem = ({ to, icon: Icon, label, badge, onClose }) => (
  <li>
    <Link to={to} onClick={onClose}
          className="flex items-center gap-2 px-2 py-1.5 rounded-md transition hover:bg-black/[0.04]"
          style={{ color: LT.greenDeep }}>
      <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: LT.green50 }} />
      <span className="text-[13px] flex-1">{label}</span>
      {badge != null && badge > 0 && (
        <span className="text-[10px] font-medium rounded-full px-1.5 py-0.5"
              style={{ background: LT.greenDeep, color: LT.beigeLight, minWidth: 18, textAlign: 'center' }}>
          {badge}
        </span>
      )}
    </Link>
  </li>
);

const HrItemExternal = ({ href, icon: Icon, label, onClose }) => (
  <li>
    <a href={href} target="_blank" rel="noreferrer" onClick={onClose}
       className="flex items-center gap-2 px-2 py-1.5 rounded-md transition hover:bg-black/[0.04]"
       style={{ color: LT.greenDeep }}>
      <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: LT.green50 }} />
      <span className="text-[13px] flex-1">{label}</span>
      <ExternalLink className="h-3 w-3" style={{ color: LT.green50 }} />
    </a>
  </li>
);

/* ── User dropdown · locale picker + dark mode + sign out ───────── */

const UserDropdown = ({
  t, user, initials, isAdmin, showBeheer, canEditContent,
  locale, setLocale, supported, labels,
  logout,
  onOpenProfile,
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button type="button"
              className="flex items-center gap-2 px-1 py-1 rounded-full transition hover:bg-black/[0.04]"
              aria-label={user?.displayName || 'User menu'}>
        <Avatar className="h-8 w-8">
          <AvatarImage src={user?.clerkImageUrl || user?.profile?.avatar} alt={user?.displayName} />
          <AvatarFallback style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}`, color: LT.greenDeep }}>
            {initials}
          </AvatarFallback>
        </Avatar>
        <ChevronDown className="h-3.5 w-3.5" style={{ color: LT.green50 }} />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56">
      <DropdownMenuLabel className="leading-tight">
        <div className="truncate">{user?.displayName || '—'}</div>
        {user?.email && <div className="text-[11px] font-normal text-[hsl(var(--text-muted))] font-num truncate">{user.email}</div>}
      </DropdownMenuLabel>
      <DropdownMenuSeparator />

      <DropdownMenuItem onSelect={() => onOpenProfile?.()} className="gap-2">
        <ImageIcon className="h-4 w-4" /> {t('profile.menu')}
      </DropdownMenuItem>

      {(showBeheer || canEditContent) && (
        <>
          <DropdownMenuSeparator />
          {showBeheer && (
            <DropdownMenuItem asChild className="gap-2">
              <Link to="/admin">
                <ShieldCheck className="h-4 w-4" /> {t('nav.adminPanel')}
              </Link>
            </DropdownMenuItem>
          )}
          {/* MANEVA shortcut · admins + content editors only. Expense
              managers reach Beheer above but not the newsletter CMS. */}
          {(isAdmin || canEditContent) && (
            <DropdownMenuItem asChild className="gap-2">
              <Link to="/admin/newsletters">
                <Newspaper className="h-4 w-4" /> {t('nav.manevaCms')}
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
        </>
      )}


      <DropdownMenuLabel className="text-[10.5px] uppercase tracking-[0.14em] text-[hsl(var(--text-muted))]">
        <span className="inline-flex items-center gap-1.5"><Languages className="h-3 w-3" /> {labels[locale]}</span>
      </DropdownMenuLabel>
      {supported.map(code => (
        <DropdownMenuItem key={code} onSelect={() => setLocale(code)} className="gap-2">
          {labels[code]}
          <span className="ml-auto text-[11px] uppercase text-[hsl(var(--text-muted))]">{code}</span>
        </DropdownMenuItem>
      ))}

      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={() => logout()} className="gap-2">
        <LogOut className="h-4 w-4" /> {t('common.signOut')}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);
